/**
 * SMS-авторизация для MAX Client
 */
import { useState } from 'react';
import { Button, Card, CardHeader, CardTitle, CardContent, Input } from '@/components/ui';
import {
  sendCode,
  verifyCode,
  type AuthStatus,
  type PhoneAuthError,
} from '@/api/maxClient';
import { X } from 'lucide-react';
import { useToast } from '@/hooks/useToast';

interface MaxPhoneAuthProps {
  onSuccess: (status: AuthStatus) => void;
  onCancel: () => void;
  tenantId?: string;
}

export function MaxPhoneAuth({ onSuccess, onCancel, tenantId }: MaxPhoneAuthProps) {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [authError, setAuthError] = useState<PhoneAuthError | null>(null);
  const { showWarning, showSuccess, showError } = useToast();

  const startResendTimer = (seconds: number) => {
    setResendTimer(seconds);
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    if (!tenantId) {
      showWarning('Выберите клинику для SMS-авторизации MAX');
      return;
    }
    if (!phone.trim()) {
      showWarning('Введите номер телефона');
      return;
    }

    setIsSending(true);
    setAuthError(null);
    try {
      const result = await sendCode(phone, tenantId);
      setPhone(result.phone);
      setStep('code');
      startResendTimer(60);
      showSuccess('SMS-код отправлен на указанный номер');
    } catch (err: any) {
      const error = err as PhoneAuthError;
      setAuthError(error);
      if (error.type === 'captcha_required') {
        showError(error.message);
      } else if (error.type === 'flood_wait') {
        showWarning(error.message);
      } else {
        showError(error.message || 'Не удалось отправить SMS-код');
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!tenantId) return;
    if (!code.trim()) {
      showWarning('Введите код из SMS');
      return;
    }

    setIsVerifying(true);
    setAuthError(null);
    try {
      const result = await verifyCode(phone, code, tenantId);
      showSuccess('MAX аккаунт успешно подключен');
      onSuccess({
        status: 'connected',
        phone: result.phone,
        user_id: result.user_id,
      });
    } catch (err: any) {
      const error = err as PhoneAuthError;
      setAuthError(error);
      if (error.type === 'code_expired') {
        setStep('phone');
        setCode('');
      }
      showError(error.message || 'Не удалось подтвердить код');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Card className="border-violet-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>SMS-авторизация MAX</CardTitle>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {step === 'phone' && (
          <>
            <p className="text-sm text-neutral-600">
              Введите номер телефона клиники в MAX. Код придёт по SMS, как в web.max.ru.
            </p>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+7 (999) 123-45-67"
              disabled={isSending}
            />
            {authError && (
              <p className="text-sm text-red-600">{authError.message}</p>
            )}
            <Button
              onClick={handleSendCode}
              variant="primary"
              className="w-full"
              isLoading={isSending}
              disabled={isSending}
            >
              Отправить SMS-код
            </Button>
          </>
        )}

        {step === 'code' && (
          <>
            <p className="text-sm text-neutral-600">
              Код отправлен на <span className="font-medium">{phone}</span>
            </p>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              maxLength={6}
              disabled={isVerifying}
            />
            {authError && (
              <p className="text-sm text-red-600">{authError.message}</p>
            )}
            <Button
              onClick={handleVerifyCode}
              variant="primary"
              className="w-full"
              isLoading={isVerifying}
              disabled={isVerifying || code.length < 4}
            >
              Подтвердить код
            </Button>
            <Button
              onClick={handleSendCode}
              variant="secondary"
              className="w-full"
              disabled={isSending || resendTimer > 0}
              isLoading={isSending}
            >
              {resendTimer > 0 ? `Отправить снова (${resendTimer}с)` : 'Отправить код снова'}
            </Button>
            <Button
              onClick={() => {
                setStep('phone');
                setCode('');
                setAuthError(null);
              }}
              variant="ghost"
              className="w-full"
            >
              Изменить номер
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
